import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { StepType } from "../common";
import {
  afterHoursConditionSchema,
  assignmentTypeConditionSchema,
  hasTagConditionSchema,
  lastOrderTimeConditionSchema,
  messageBodyMatchesConditionSchema,
  messageSentimentConditionSchema,
  receivedAtEmailConditionSchema,
  ruleActionSchema,
  sentFromEmailConditionSchema,
  subjectLineConditionSchema,
  topicConditionSchema,
} from "../rules/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleConditionType,
  selectionQueryOperators,
} from "../rules/types";
import { CONVERSATION_STATUSES_V3, MESSAGE_CHANNELS_V3 } from "./types";

// V3-specific: TICKET_CHANNEL uses MessageChannelV3 strings
export const ticketChannelV3ConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.TICKET_CHANNEL),
  channels: z.array(z.enum(MESSAGE_CHANNELS_V3)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export type TicketChannelV3Condition = z.infer<
  typeof ticketChannelV3ConditionSchema
>;

// V3-specific: TICKET_STATUS uses ConversationStatusV3 strings
export const ticketStatusV3ConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.TICKET_STATUS),
  statuses: z.array(z.enum(CONVERSATION_STATUSES_V3)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export type TicketStatusV3Condition = z.infer<
  typeof ticketStatusV3ConditionSchema
>;

export const ruleV3ConditionSchema = z.union([
  ticketChannelV3ConditionSchema,
  ticketStatusV3ConditionSchema,
  topicConditionSchema,
  sentFromEmailConditionSchema,
  receivedAtEmailConditionSchema,
  subjectLineConditionSchema,
  messageBodyMatchesConditionSchema,
  afterHoursConditionSchema,
  assignmentTypeConditionSchema,
  lastOrderTimeConditionSchema,
  messageSentimentConditionSchema,
  hasTagConditionSchema,
]);
export type RuleV3Condition = z.infer<typeof ruleV3ConditionSchema>;

const conditionGroupV3Operator = z.enum(["AND", "OR"]);
export type ConditionGroupV3Operator = z.infer<typeof conditionGroupV3Operator>;

const conditionGroupV3Schema_: z.ZodType<ConditionGroupV3> = z.lazy(() =>
  z.object({
    operator: conditionGroupV3Operator,
    conditions: z.array(
      z.union([ruleV3ConditionSchema, conditionGroupV3Schema_]),
    ),
  }),
);
export const conditionGroupV3Schema: z.ZodType<ConditionGroupV3> =
  conditionGroupV3Schema_;

export interface ConditionGroupV3 {
  operator: ConditionGroupV3Operator;
  conditions: (RuleV3Condition | ConditionGroupV3)[];
}

// Base step schema
const baseStepSchema = z.object({
  customTitle: z.string().optional(),
  type: z.string(),
  id: z.string(),
});

const ruleV3ConditionsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RULE_CONDITIONS_V3),
  conditionGroups: conditionGroupV3Schema,
  nextTrueId: z.string().optional(),
  nextFalseId: z.string().optional(),
});
export const ruleV3ConditionsStepSchema: z.ZodType<RuleV3ConditionsStep> =
  ruleV3ConditionsStepSchema_;
export interface RuleV3ConditionsStep
  extends z.infer<typeof ruleV3ConditionsStepSchema_> {}

const ruleV3ActionsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RULE_ACTIONS_V3),
  actions: z.array(ruleActionSchema),
  errorHandlingStrategy: createEnum(ErrorHandlingStrategy).default(
    ErrorHandlingStrategy.FAIL_FAST,
  ),
  nextId: z.string().optional(),
});
export const ruleV3ActionsStepSchema: z.ZodType<RuleV3ActionsStep> =
  ruleV3ActionsStepSchema_;
export interface RuleV3ActionsStep
  extends z.infer<typeof ruleV3ActionsStepSchema_> {}
